import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Link,
  IconButton,
  InputAdornment,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { forgotPassword } from "../services/authService";
import {
  authColors,
  authTitleSx,
  authSubtitleSx,
  authLabelSx,
  authTextFieldSx,
  authInputIconSx,
  authLinkAccentSx,
  authPrimaryButtonSx,
  authPanelFlexSx,
  authCloseButtonSx,
  authFieldErrorSx,
  authInfoAlertSx,
  authInfoAlertTextSx,
  isValidEmail,
} from '../styles/authPanel.styles';

function FieldLabel({ children }) {
  return (
    <Typography component="label" sx={authLabelSx}>
      {children}
    </Typography>
  );
}

const forgotTitleSx = {
  ...authTitleSx,
  mt: 0,
  mb: 0,
};

const forgotSubtitleSx = {
  ...authSubtitleSx,
  mt: '12px',
  mb: 0,
  pr: 2,
};

const forgotPanelSx = {
  ...authPanelFlexSx,
  height: '100%',
  minHeight: 0,
  overflowY: 'auto',
};

const forgotContentSx = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  width: '100%',
};

const forgotHeaderSx = {
  flexShrink: 0,
  width: '100%',
  mb: { xs: 2, sm: 2.5 },
};

const forgotBodySx = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  width: '100%',
  minHeight: 0,
  gap: 3,
};

const forgotBackLinkSx = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '6px',
  width: '100%',
  flexShrink: 0,
};

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [error, setError] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
    if (emailError) setEmailError("");
    if (error) setError("");
  };

  const handleEmailBlur = () => {
    if (!email.trim()) {
      setEmailError("");
      return;
    }
    if (!isValidEmail(email)) {
      setEmailError("Ingresá un email válido");
    } else {
      setEmailError("");
    }
  };

  const canSubmit = email.trim() !== "" && isValidEmail(email) && !loading;

  const handleSendCode = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    setLoading(true);
    setError("");

    try {
      const result = await forgotPassword(email);

      if (result.success) {
        setEmailSent(true);
        return;
      }

      console.error("forgotPassword failed:", result.error);
      setError(result.error || "Error al enviar código de verificación");
    } catch (err) {
      console.error("Exception during forgot password:", err);
      setError(err.message || "Error al enviar código de verificación");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper elevation={0} sx={forgotPanelSx}>
      <IconButton
        size="small"
        onClick={() => navigate("/app")}
        aria-label="Cerrar"
        sx={authCloseButtonSx}
      >
        <CloseIcon sx={{ fontSize: 22 }} />
      </IconButton>

      <Box sx={forgotContentSx}>
        <Box sx={forgotHeaderSx}>
          <Typography component="h1" sx={forgotTitleSx}>
            ¿Olvidaste tu contraseña?
          </Typography>
          <Typography component="p" sx={forgotSubtitleSx}>
            {emailSent
              ? 'Te enviamos un correo con un link para crear tu nueva contraseña.'
              : 'Ingresa tu email y te enviaremos instrucciones para recuperarla.'}
          </Typography>
        </Box>

        <Box sx={forgotBodySx}>
          {emailSent ? (
            <Box sx={{ ...authInfoAlertSx, mb: 0, width: '100%' }} role="status">
              <InfoOutlinedIcon sx={{ fontSize: 20, color: authColors.textWhite, flexShrink: 0 }} />
              <Typography sx={{ ...authInfoAlertTextSx, minWidth: 0, wordBreak: 'break-word' }}>
                Revisá tu correo en <strong>{email}</strong>.
              </Typography>
            </Box>
          ) : (
            <Box
              component="form"
              onSubmit={handleSendCode}
              sx={{ width: '100%' }}
            >
              <Box sx={{ mb: '16px' }}>
                <FieldLabel>Email o usuario</FieldLabel>
                <TextField
                  placeholder="usuario@email.com"
                  variant="outlined"
                  fullWidth
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={handleEmailBlur}
                  disabled={loading}
                  autoComplete="email"
                  error={!!emailError}
                  sx={authTextFieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ ml: '6px', mr: '4px' }}>
                        <MailOutlineIcon sx={authInputIconSx} />
                      </InputAdornment>
                    ),
                  }}
                />
                {emailError && (
                  <Typography sx={authFieldErrorSx}>{emailError}</Typography>
                )}
              </Box>

              {error && (
                <Typography sx={{ ...authFieldErrorSx, mb: '12px' }}>
                  {error}
                </Typography>
              )}

              <Box sx={{ mt: '24px', width: '100%' }}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disableElevation
                  disabled={!canSubmit}
                  sx={authPrimaryButtonSx}
                >
                  {loading ? "Enviando..." : "Enviar email de recuperación"}
                </Button>
              </Box>
            </Box>
          )}

          <Box sx={forgotBackLinkSx}>
            <ArrowBackIcon sx={{ fontSize: 18, color: authColors.linkAccent, flexShrink: 0 }} />
            <Link
              component="button"
              type="button"
              onClick={() => navigate("/login")}
              sx={{
                ...authLinkAccentSx,
                fontSize: '16px',
                lineHeight: '20px',
              }}
            >
              Volver al login
            </Link>
          </Box>
        </Box>

      </Box>
    </Paper>
  );
};

export default ForgotPassword;
